import NextAuth, { NextAuthOptions } from "next-auth";
import type { Account, Session } from "next-auth";
import KeycloakProvider from "next-auth/providers/keycloak";
import type { JWT } from "next-auth/jwt";
import { jwtDecode } from "jwt-decode";

interface AuthConfig {
  keycloakId: string;
  keycloakSecret: string;
  keycloakIssuer: string;
  nextAuthSecret: string;
  nextAuthUrl: string;
}

const refreshAccessToken = async (token: JWT): Promise<JWT> => {
  try {
    const formData = new URLSearchParams({
      client_id: process.env.KEYCLOAK_CLIENT_ID!,
      client_secret: process.env.KEYCLOAK_CLIENT_SECRET!,
      grant_type: "refresh_token",
      refresh_token: token.refreshToken!,
    });

    const response = await fetch(
      `${process.env.KEYCLOAK_ISSUER}/protocol/openid-connect/token`,
      {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData,
      },
    );

    if (!response.ok) {
      throw await response.json();
    }

    const refreshedTokens = await response.json();
    const decoded: { exp: number } = jwtDecode(refreshedTokens.access_token);
    const now = Date.now();

    return {
      ...token,
      accessToken: refreshedTokens.access_token,
      refreshToken: refreshedTokens.refresh_token ?? token.refreshToken,
      accessTokenExpires: decoded.exp * 1000,
      refreshTokenExpires: now + refreshedTokens.refresh_expires_in * 1000,
      error: undefined,
    };
  } catch (err) {
    return { ...token, error: "RefreshAccessTokenError" };
  }
};

export function createAuthOptions(): NextAuthOptions {
  return {
    secret: process.env.NEXTAUTH_SECRET,
    session: {
      strategy: "jwt",
      maxAge: 24 * 60 * 60, // 24 hours
    },
    providers: [
      KeycloakProvider({
        clientId: process.env.KEYCLOAK_CLIENT_ID!,
        clientSecret: process.env.KEYCLOAK_CLIENT_SECRET!,
        issuer: process.env.KEYCLOAK_ISSUER!,
        authorization: {
          params: {
            scope: "openid email profile",
          },
        },
      }),
    ],
    callbacks: {
      async signIn() {
        return true;
      },

      async jwt({
        token,
        account,
      }: {
        token: JWT;
        account: Account | null;
      }): Promise<JWT> {
        const now = Date.now();

        if (account) {
          const decoded: { exp: number } = jwtDecode(account.access_token!);
          token.accessToken = account.access_token;
          token.refreshToken = account.refresh_token;
          token.accessTokenExpires = decoded.exp * 1000;
          token.refreshTokenExpires =
            now + (Number(account.refresh_expires_in) || 1800) * 1000;
          return token;
        }

        if (
          token.accessTokenExpires !== undefined &&
          now < token.accessTokenExpires
        )
          return token;

        if (
          token.refreshTokenExpires !== undefined &&
          now > token.refreshTokenExpires
        )
          return { ...token, error: "RefreshAccessTokenExpired" };

        return await refreshAccessToken(token);
      },

      async redirect({ url, baseUrl }) {
        const isAbsoluteUrl =
          url.startsWith("http://") || url.startsWith("https://");
        const urlObj = new URL(isAbsoluteUrl ? url : baseUrl + url);

        if (urlObj.searchParams.has("redirect_to")) {
          return url;
        }

        const callbackUrl = urlObj.searchParams.get("callbackUrl");
        if (callbackUrl && callbackUrl !== "/auth") {
          return `/auth?redirect_to=${encodeURIComponent(callbackUrl)}`;
        }

        const path = urlObj.pathname;
        if (path !== "/auth") {
          return `/auth?redirect_to=${encodeURIComponent(path)}`;
        }

        return "/auth";
      },

      async session({
        session,
        token,
      }: {
        session: Session;
        token: JWT;
      }): Promise<Session> {
        session.accessToken = token.accessToken;
        session.user.id = token.sub as string;
        session.error = token.error;

        return session;
      },
    },
    events: {
      async signOut({ token }) {
        try {
          await fetch(
            `${process.env.KEYCLOAK_ISSUER}/protocol/openid-connect/logout`,
            {
              method: "POST",
              headers: { "Content-Type": "application/x-www-form-urlencoded" },
              body: new URLSearchParams({
                client_id: process.env.KEYCLOAK_CLIENT_ID!,
                client_secret: process.env.KEYCLOAK_CLIENT_SECRET!,
                refresh_token: token.refreshToken as string,
              }),
            },
          );
        } catch (err) {
          console.error("Failed to log out from Keycloak", err);
        }
      },
    },
    pages: {
      signIn: "/auth",
      error: "/api/auth/error",
    },
  };
}
